import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import moment from 'moment-timezone';
import Prism from 'prismjs';
import Editor from '@tinymce/tinymce-vue';
import dropsLoader from '@/mixins/components/drops-loader';
import { modals } from '@/mixins/modal';
import fontColors from '@editor/droplets/_bubble-options/components/inline-text/font-colors.json';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import { DEFAULT_PROFILE_PIC } from '@/constants';
import CloseTicket from './close-ticket';

export default Vue.component('admin-support-ticket-view', {
  template: require('./template.html'),
  name: 'admin-support-ticket-view',
  computed: {
    ...mapGetters(['currentUser', 'ticketUsers']),
    colors() {
      const list = [];
      _.forEach(fontColors, (hex) => {
        list.push(hex, hex);
      });
      return list;
    },
  },
  components: {
    dropsLoader,
    uploadProgress,
    Editor,
    CloseTicket,
  },
  mixins: [guid, modals, uploader],
  props: ['active-ticket', 'tiny-mce'],
  data() {
    return {
      uuid: 'view-ticket-editor',
      $tinymce: null,
      bucketPath: '',
      tinyLoader: false,
      hideTextEditor: true,
      commentMessage: '',
      loading: false,
      userLoading: false,
      ticket: {},
      addComment: false,
      submitError: false,
      loadError: false,
      modals: {
        closeTicket: false,
      },
    };
  },
  created() {
    this.hideTextEditor = false;
    this.tinyLoader = true;
    this.loadTicket();
  },
  mounted() {
    this.cls = '.adminUi-modalOverlay';
    if (!this.loadError) {
      $(document).ready(this.highlightCode());
    }
  },
  methods: {
    initEditor() {
      this.$tinymce = tinymce;
      this.tinyLoader = false;
    },
    async submitComment() {
      this.submitError = false;
      if (this.commentMessage.replace(/\s/g, '') === '') {
        this.submitError = 'Comment cannot be empty.';
        return;
      }
      const notification = {
        id: this.guid(),
        type: 'success',
        message: 'Comment added successfully!',
      };
      this.tinyLoader = true;
      try {
        const created = moment(moment.utc().format())._i;
        const comment = {
          _id: this.guid(),
          created,
          status: 'public',
          attachments: [],
          author: this.currentUser._id,
          message: this.commentMessage,
          plainText: this.$tinymce.activeEditor.getContent({ format: 'text' }),
        };
        const ticket = this.ticket;
        await this.$lightning([{
          name: 'submitTicketComment',
          payload: { ticketId: ticket._id, comment },
        }]);
        this.$store.dispatch('runStateFn', {
          fn() {
            ticket.comments.splice(0, 0, comment);
          },
        });
        this.commentMessage = '';
        this.addComment = false;
      } catch (e) {
        const message = e.message || e;
        notification.type = 'error';
        notification.message = message;
        this.$log.error(e);
      } finally {
        this.tinyLoader = false;
        this.$store.dispatch('addNotification', notification);
        this.highlightCode();
      }
    },
    getAuthor(comment) {
      return this.ticketUsers[comment.author] || {
        pic: DEFAULT_PROFILE_PIC,
        fullName: 'Drzzle Team Member',
      };
    },
    uploadImage(cb) {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      const self = this;
      input.onchange = async function onFileChange() {
        const file = this.files[0];
        const title = file.name;
        const filePath = `${self.ticket._id}/${path.basename(title)}`;
        const progress = self.$refs.progress.$el;

        if (progress) {
          // move the progress component into the tinyMce image upload modal
          const modalContent = document.querySelectorAll('[id^=dialog-describe_] .tox-dialog__body-content .tox-form')[0];
          modalContent.insertBefore(progress, modalContent.firstChild);
        }

        const upload = await self.uploadFile({
          file: filePath,
          body: file,
          Bucket: process.env.S3_TICKET_ASSETS_BUCKET,
          ACL: 'public-read',
          bucketPath: '',
          copyToOwner: false,
        });

        cb(upload.Location, { title });
      };
      input.click();
    },
    toggleModal(type, c) {
      this.modals[type] = !this.modals[type];
      const content = !c ? this.$refs[type] : this.$refs[type].$el;
      if (this.modals[type]) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    async getUsers() {
      this.userLoading = true;
      try {
        const commentUsers = _.uniqBy(this.ticket.comments, 'author');
        const needed = commentUsers.filter(c => !this.ticketUsers[c.author]);
        if (needed.length > 0) {
          const { users } = await this.$lightning([{
            name: 'getTicketUsers',
            payload: { ticketId: this.ticket._id, needed },
          }]);
          users.forEach((user) => {
            this.$store.dispatch('setTicketUser', {
              id: user._id,
              data: user,
            });
          });
        }
        const { _id, fullName, pic } = this.currentUser;
        if (!this.ticketUsers[_id]) {
          this.$store.dispatch('setTicketUser', {
            id: _id,
            data: {
              _id,
              pic: pic || DEFAULT_PROFILE_PIC,
              fullName,
            },
          });
        }
      } catch (e) {
        const message = e.messge || e;
        this.loadError = message;
        this.$log.error(message);
      } finally {
        this.userLoading = false;
      }
    },
    async loadTicket() {
      if (this.activeTicket) {
        this.ticket = this.activeTicket;
      } else {
        await this.getTicket({
          id: this.$router.currentRoute.params.id,
        });
      }
      await this.getUsers();
    },
    async getTicket({ id }) {
      this.loading = true;
      try {
        const { ticket } = await this.$lightning([{
          name: 'getSupportTicket',
          payload: { ticketId: id },
        }]);
        this.highlightCode();
        this.ticket = ticket;
      } catch (e) {
        const message = e.messge || e;
        this.loadError = message;
        this.$log.error(message);
      } finally {
        this.loading = false;
      }
    },
    highlightCode() {
      this._debounce(() => {
        const commentWrap = document.getElementById('adminUi-ticket-container');
        if (commentWrap) {
          Prism.highlightAllUnder(commentWrap);
        }
      }, 500);
    },
    formatTime(time) {
      return moment.utc(time).local().format('MMM DD, YYYY h:mm a');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/support/tickets/view/index.js